// Viewing Room guest screens: words verbatim from the approved
// docs/design/tuw/viewing-room/wording.md (Marco, 2026-10-31). A string that
// does not fit its slot goes back to Codex Astra for a shorter version; never
// reword here.
import { REGISTRATION, WORDING } from "@/lib/openHouse/wording"

export const VR = {
  nav: {
    findFreelancers: "Find freelancers",
    postProject: "Post a project",
    signIn: "Sign in",
    postFree: "Post a project free",
    homeLabel: "The Urban Writers home",
    openMenu: "Open menu",
    closeMenu: "Close menu",
  },
  search: {
    title: "Find Freelancers",
    intro: "Browse profiles, read samples and compare freelancers before creating an account.",
    placeholder: "Search by pen name, niche or keyword…",
    emptyTitle: "No freelancers match your search",
    emptyBody: "Try a different search or clear a filter to see more freelancers.",
    clearFilters: "Clear filters",
    viewProfile: "View profile",
    moreFiltersDescription: "Narrow the roster by tier, rating and language.",
  },
  compare: {
    button: "Compare",
    active: "Comparing",
    trayTitle: "Compare %{count} freelancers",
    trayDescription: "Compare ratings, niches and availability",
    clear: "Clear",
    title: "Compare freelancers",
    selected: "%{count} selected",
    remove: "Remove %{pen_name} from comparison",
    close: "Close comparison",
    limit: "You can compare up to %{maximum} freelancers. Remove one to add another.",
  },
  profile: {
    availabilityLabel: "Availability",
    availableNow: "Available now",
    availableFrom: "Available from %{date} (estimated)",
    availabilityUnknown: "Availability unknown",
    writerLevel: "Writer level",
    ratingOnly: "Rating only",
    noReviews: "No reviews yet",
    noReviewsBody: "%{pen_name} doesn’t have any customer reviews yet.",
    noPublicSamples: "No samples are available to view here.",
    departedNotice: "This freelancer is no longer on TUW. Browse other freelancers below.",
    unavailableNotice: "This freelancer’s profile isn’t available right now. Browse other freelancers below.",
  },
  action: {
    invite: "Invite",
    message: "Message",
    sample: "Request sample",
    video: "Video call",
    save: "Save",
  },
  // §5 and §6: the account-only box and its waiting screen. Lines the wording
  // reuses from Open House's guest wording come from there (enrollment.js).
  account: {
    save: {
      title: "Create an account to save %{pen_name}",
      body: "Confirm your email and we’ll add %{pen_name} to your favorites.",
      button: "Create account to save",
    },
    message: {
      title: "Create an account to message %{pen_name}",
      body: "Confirm your email, then write your message.",
      button: "Create account to message",
    },
    sample: {
      title: "Create an account to request a sample",
      body: "After confirming your email, add your sample details and review the price for %{pen_name}.",
      button: "Create account to continue",
    },
    video: {
      title: "Create an account for a video call",
      body: "Confirm your email, then continue setting up a call with %{pen_name}.",
      button: "Create account to continue",
    },
    dismiss: "Keep browsing",
    close: "Close",
    submitting: "Creating your account…",
    confirmationRequired: "Confirm your email before using your account.",
    agreementRequired: "Read and accept the registration policies before creating your account.",
    validationSummary: "Check the highlighted fields before continuing.",
    existingBody: "Sign in to continue where you left off.",
    failedBody: "Check your details and try again.",
  },
  confirmation: {
    title: "Confirm your email to continue",
    next: {
      message: "Confirm your email and we’ll open your conversation with %{pen_name}. You can write your message there.",
      save: "Confirm your email and we’ll save %{pen_name} to your favorites.",
      sample:
        "Confirm your email to continue your sample request with %{pen_name}. You’ll still need to add the details and accept the price.",
      video: "Confirm your email and we’ll return you to call setup with %{pen_name}. Your call hasn’t started.",
    },
    submit: "Confirm email and continue",
    deliveryFailed: "We couldn’t send your confirmation email. Check the address and try sending it again.",
    alreadyConfirmedBody: "Continue where you left off.",
    alreadyConfirmedButton: "Continue",
    confirmedElsewhere: "Your email is confirmed. Sign in here to continue where you left off.",
    noPendingAccount:
      "There’s no account waiting for confirmation here. If you’ve already confirmed your email, sign in to continue.",
    accountConflict: "We couldn’t confirm this account. Contact TUW support for help.",
    contactSupport: "Contact TUW support",
    emailUnchanged: "This is already your confirmation email address. You can resend the email from the previous screen.",
    emailInUseBody: "Sign in to that account to continue where you left off.",
    // An unconfirmed account deleted at its deadline (Sundown).
    accountExpired: {
      title: "This unconfirmed account has expired",
      body: "We didn’t receive email confirmation within %{days} days, so we deleted your unconfirmed account. Nothing was sent to any freelancer.",
      recovery: "Create your account again to continue. Your earlier choice, such as saving or messaging a freelancer, wasn’t kept.",
      oldLink: "This code or link no longer works because the unconfirmed account was deleted.",
      startAgain: "Start again",
    },
  },
  resumed: {
    saved: "%{pen_name} is saved to your favorites.",
    savedButton: "Saved to favorites",
    message: "Your conversation with %{pen_name} is open. Write your message when you’re ready.",
    video: "Continue setting up your call with %{pen_name}.",
    sample: "Continue your sample request with %{pen_name}. Add the details and accept the price before sending it.",
    sampleNotSent: "Your sample request hasn’t been sent.",
    sampleContinue: "Continue sample request",
    freelancerUnavailable: "Your account is ready, but this freelancer is no longer available for this action.",
    browse: "Find another freelancer",
    actionMissing: "Your account is ready. Return to the freelancer’s profile to continue.",
  },
  // §8: Invite into Open House. The open-levels lines reuse Posting wording §24.
  invite: {
    strip: "Invite %{pen_name} to request your project",
    pending: "We’ll send the invitation after you confirm your email, if %{pen_name} can still take the project.",
    otherFreelancers: "Other eligible freelancers can request your project too.",
    notAssigned: "An invitation doesn’t assign the freelancer to your project.",
    viewProfile: "View profile",
    remove: "Remove invitation",
    removed: "Invitation removed. You can continue with your project.",
    levelPrefilled: "We’ve selected %{writer_level} to match %{pen_name}’s writer level. You can change it.",
    levelExcluded:
      "%{pen_name} can’t request this project at the writer level you’ve selected. Continue without the invitation, or change the level to include them.",
    restoreLevel: "Use %{invited_writer_level}",
    continueWithout: "Continue without inviting %{pen_name}",
    levelRestored: "%{pen_name} can request this project at the selected writer level.",
    openLevels: WORDING.openLevels,
    openLevelsDescription: WORDING.openLevelsDesc,
    openLevelsFinalPrice: WORDING.openLevelsTotal,
    openLevelsPosted: WORDING.openLevelsPosted,
    openLevelsIncluded: "%{pen_name} can still request your project when it’s open to every writer level.",
    editorSelected: "Selected editor: %{pen_name}",
  },
  // §9: what became of the invitation, once confirmation published the
  // project. viewProject reuses Guest wording §8, `published.viewProject`.
  invitation: {
    sent: {
      title: "Your project is live",
      body: "We’ve invited %{pen_name} to request your project.",
      next: "You’ll review their request before deciding whether to work with them.",
    },
    departed: {
      title: "Your project is live",
      body: "%{pen_name} is no longer on TUW, so we couldn’t send the invitation.",
      next: "You can invite another freelancer or review requests as they arrive.",
    },
    unavailable: {
      title: "Your project is live",
      body: "We couldn’t invite %{pen_name} because they can’t take this project.",
      next: "You can invite another freelancer or review requests as they arrive.",
    },
    pending: { body: "Your project is live. We’re still processing the invitation to %{pen_name}." },
    failed: {
      body: "Your project is live, but we couldn’t send the invitation to %{pen_name}. Try inviting them from your project.",
    },
    viewProject: "View my project",
    findAnother: "Find another freelancer",
  },
  // §10: a service Open House does not cover. signIn reuses Guest wording §3,
  // `registration.existingAccount.button`.
  handoff: {
    title: "Continue with %{service_name}",
    body: "Create an account or sign in, then complete the %{service_name} form to invite %{pen_name}.",
    continue: "Continue",
    signIn: REGISTRATION.existingButton,
    back: "Back to profile",
  },
  error: {
    browseLimitedTitle: "Please pause before browsing more profiles",
    browseLimitedBody:
      "There have been too many browsing requests from your connection. Try again in %{retry_after_seconds} seconds.",
    generic: "Something went wrong. Please try again.",
    intentLimited: "There has been too much activity from this connection. Try again in about an hour.",
    search: "We couldn’t load the freelancers. Please try again.",
    profile: "We couldn’t load this profile. Please try again.",
    save: "We couldn’t save %{pen_name} to your favorites. Please try again.",
    message: "We couldn’t open your conversation with %{pen_name}. Please try again.",
    video: "We couldn’t open call setup with %{pen_name}. Please try again.",
    sample: "We couldn’t open your sample request with %{pen_name}. Please try again.",
    retry: "Try again",
    backToProfile: "Back to profile",
    backToSearch: "Back to search",
    contactLimitedTitle: "Please wait before trying again",
    contactLimitedBody:
      "You’ve reached the limit for starting contact with freelancers. Try again in %{retry_after_seconds} seconds.",
    confirmationLimited: "There have been too many confirmation attempts. Try again in %{retry_after_seconds} seconds.",
    confirmationEmailLimit:
      "We can’t send another confirmation email for this account right now. Contact TUW support for help.",
    codeAttemptsExhausted: "You’ve entered the wrong code too many times. Request another confirmation email.",
    freelancerContactLimited:
      "You’ve reached the contact limit for this freelancer. Try again in %{retry_after_seconds} seconds.",
  },
}

// Fills %{name} slots.
export function fill(text, values = {}) {
  return text.replace(/%\{(\w+)\}/g, (_, name) => String(values[name] ?? ""))
}

// The server's notice keys (vr.profile.departed_notice / unavailable_notice).
export const NOTICES = {
  "vr.profile.departed_notice": VR.profile.departedNotice,
  "vr.profile.unavailable_notice": VR.profile.unavailableNotice,
}
